import React, { useEffect, useState, useCallback } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import './App.scss';
import { init, drawPlanet } from './scripts/main';
import satSet from './scripts/sat';
import InfoBox from './InfoBox/InfoBox';
import Groups from './Groups';
import Legend from './Legend';
import References from './References';
import Search from './Search';
import BeyondTheEarthLogo from './images/beyond_the_earth_logo.png';
import EuroplanetLogo from './images/europlanet_logo.png';

const LeftPanel = {
	LEGEND: 'LEGEND',
	REFERENCES: 'REFERENCES',
	SEARCH: 'SEARCH',
};

export default () => {
	const [planet, setPlanet] = useState('EARTH');
	const [activeSatelliteId, setActiveSatalliteId] = useState(null);
	const [groups, setGroups] = useState([]);

	useEffect(init, []);
	useEffect(() => drawPlanet(planet), [planet]);

	// Left hand side
	const [leftPanel, setLeftPanel] = useState(null);

	// Handle menus
	const handleLegendClick = useCallback(() => {
		setLeftPanel(leftPanel !== LeftPanel.LEGEND ? LeftPanel.LEGEND : null);
	}, [leftPanel]);
	const handleReferencesClick = useCallback(() => {
		setLeftPanel(
			leftPanel !== LeftPanel.REFERENCES ? LeftPanel.REFERENCES : null
		);
	}, [leftPanel]);
	const handleSearchClick = useCallback(() => {
		setLeftPanel(leftPanel !== LeftPanel.SEARCH ? LeftPanel.SEARCH : null);
	}, [leftPanel]);

	window.openSearch = () => setLeftPanel(LeftPanel.SEARCH);
	window.closeLeftPanel = () => setLeftPanel(null);

	// Handle canvas
	const handleCanvasClick = useCallback(() => {
		setLeftPanel(null);
	}, []);

	// Handle planet menus
	const handleEarthClick = useCallback(() => {
		setPlanet('EARTH');
		setLeftPanel(null);
	}, []);
	const handleMoonClick = useCallback(() => {
		setPlanet('MOON');
		setLeftPanel(null);
	}, []);
	const handleMarsClick = useCallback(() => {
		setPlanet('MARS');
		setLeftPanel(null);
	}, []);
	const handleVenusClick = useCallback(() => {
		setPlanet('VENUS');
		setLeftPanel(null);
	}, []);
	const handleMercuryClick = useCallback(() => {
		setPlanet('MERCURY');
		setLeftPanel(null);
	}, []);

	const selectSatellite = useCallback(setActiveSatalliteId, [
		setActiveSatalliteId,
	]);

	useEffect(() => {
		document.addEventListener('click', () => {
			document.getElementById('background_music').play();
		});
	}, []);

	// Force refresh
	const [, updateState] = useState();
	const forceUpdate = useCallback(() => updateState({}), []);
	useEffect(() => {
		const interval = window.setInterval(forceUpdate, 500);
		return () => window.clearInterval(interval);
	}, [forceUpdate]);

	window.selectSatellite = selectSatellite;
	window.setGroups = setGroups;

	const activeSatellite = satSet.getSat(activeSatelliteId);

	return (
		<>
			<div id="canvas-holder">
				<canvas id="canvas" onMouseDown={handleCanvasClick} />
				{planet !== 'EARTH' && (
					<div className="construction-banner">
						Heritage model under construction
					</div>
				)}

				<div id="menu-left" className="menubar">
					<div id="search-holder" className="menu-item">
						<span className="icon-search" />
						<input type="search" id="search" autoComplete="off" />
					</div>
					<Groups groups={groups} />
					<div className="menu-item">
						<div
							className={`menu-title ${
								leftPanel === LeftPanel.LEGEND ? 'active' : ''
							}`}
							onClick={handleLegendClick}
						>
							Legend
						</div>
					</div>
					<div className="menu-item">
						<div className="menu-title">Nominations</div>
					</div>
					<div className="menu-item">
						<div
							className={`menu-title ${
								leftPanel === LeftPanel.REFERENCES
									? 'active'
									: ''
							}`}
							onClick={handleReferencesClick}
						>
							References
						</div>
					</div>
					<div className="menu-item">
						<div className="menu-title">Decays</div>
					</div>
				</div>
				<div id="menu-right" className="menubar">
					<div className="menu-item" onClick={handleEarthClick}>
						<div
							className={`menu-title ${
								planet === 'EARTH' ? 'active' : ''
							}`}
						>
							Earth
						</div>
					</div>
					<div className="menu-item" onClick={handleMarsClick}>
						<div
							className={`menu-title ${
								planet === 'MARS' ? 'active' : ''
							}`}
						>
							Mars
						</div>
					</div>
					<div className="menu-item" onClick={handleMercuryClick}>
						<div
							className={`menu-title ${
								planet === 'MERCURY' ? 'active' : ''
							}`}
						>
							Mercury
						</div>
					</div>
					<div className="menu-item" onClick={handleMoonClick}>
						<div
							className={`menu-title ${
								planet === 'MOON' ? 'active' : ''
							}`}
						>
							Moon
						</div>
					</div>
					<div className="menu-item" onClick={handleVenusClick}>
						<div
							className={`menu-title ${
								planet === 'VENUS' ? 'active' : ''
							}`}
						>
							Venus
						</div>
					</div>
					<div className="menu-item">
						<div className="menu-title">Submit</div>
					</div>
					<div id="menu-about" className="menu-item">
						<div className="menu-title">About</div>
						<div id="about-box" className="menubox submenu">
							<span className="box-header">
								Heritage in Space
							</span>
							<p>
								Heritage in Space is an active catalogue and
								real-time 3D map of multinational objects in
								Earth orbit and in proximity to other
								astronomical bodies, visualised using{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href="https://experiments.withgoogle.com/chrome/globe"
								>
									WebGL
								</a>{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href="https://www.space-track.org/auth/login"
								>
									Space-Track
								</a>{' '}
								and ESA’s{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href="https://discosweb.esoc.esa.int"
								>
									DISCOS
								</a>{' '}
								repository alongside supplementary tracking data
								provided by ESA’s{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href="https://www.fd-tasc.info/roscmd/"
								>
									TASC
								</a>{' '}
								and NASA’s{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href="https://ssd.jpl.nasa.gov/horizons.cgi#top"
								>
									Horizons
								</a>{' '}
								services. The platform has been developed by the{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href="https://www.beyondtheearth.org/"
								>
									{' '}
									Beyond the Earth foundation
								</a>{' '}
								using public engagement funding kindly provided
								by the{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href="http://www.europlanet-2020-ri.eu/"
								>
									Europlanet planetary science consortium
								</a>
								.
							</p>

							<p>
								This index is consolidated as an active-document
								open for communal input in order to initially
								peer-identify and track off-world infrastructure
								that may be the focus of future international
								heritage protection policies within formal
								registries. All nominated objects are
								categorised under extended heritage definitions
								initially established by the principles of the{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href="https://en.wikipedia.org/wiki/Burra_Charter"
								>
									Burra Charter
								</a>
								.
							</p>
							<p>
								Soundtrack ‘Your Outer World’ kindly provided by{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href="https://www.youtube.com/channel/UCcavSftXHgxLBWwLDm_bNvA"
								>
									CO.AG Music
								</a>
								.
							</p>

							<p>Earth index</p>
							<p>
								The present Earth index and visualisation is
								assembled as a tool for the peer-identification
								and subsequent tracking of historic artefacts in
								geocentric orbit which exhibit ‘significance’
								for space archaeology studies. This experimental
								platform intends to collate public nominations,
								memories and oral histories of satellite
								infrastructure with the intention of thereafter
								ascertaining whether these distinguished objects
								may feasibly be preserved in-situ in compliance
								with inter-agency debris mitigation guidelines
								for GSO and LEO regions. The interactive
								platform is based on coding from the{' '}
								<a
									href="http://stuffin.space"
									rel="noreferrer noopener"
									target="_blank"
								>
									stuffin.space
								</a>{' '}
								website (see; Debris) and updates daily with
								orbital data from{' '}
								<a
									href="https://www.space-track.org"
									rel="noreferrer noopener"
									target="_blank"
								>
									Space-Track
								</a>{' '}
								and{' '}
								<a
									href="https://discosweb.esoc.esa.int/"
									rel="noreferrer noopener"
									target="_blank"
								>
									DISCOS
								</a>{' '}
								in conjunction with the excellent{' '}
								<a
									href="https://github.com/shashwatak/satellite-js"
									rel="noreferrer noopener"
									target="_blank"
								>
									satellite.js
								</a>{' '}
								JavaScript library to calculate satellite
								positions from TLE files. Additional tracking
								resources have been acquired from{' '}
								<a
									href="https://theskylive.com/"
									rel="noreferrer noopener"
									target="_blank"
								>
									The Sky Live
								</a>
								. The present catalogue details significant
								objects arising from the first 40 years of space
								exploration (1957 – 1997).
							</p>

							<p>Mars, Moon, Venus, Mercury Indices</p>
							<p>
								These visualisations independently document
								items of space heritage (historic and
								contemporary hardware) that are estimated to
								remain in orbit around other astronomical bodies
								or reside on the surface of these celestial
								objects. These visualisations update daily using
								data provided by ESA’s{' '}
								<a
									href="https://www.fd-tasc.info/roscmd/"
									rel="noreferrer noopener"
									target="_blank"
								>
									TASC
								</a>{' '}
								and NASA’s{' '}
								<a
									href="https://ssd.jpl.nasa.gov/horizons.cgi"
									rel="noreferrer noopener"
									target="_blank"
								>
									Horizons
								</a>{' '}
								services. For an elaborated Luna index, see the{' '}
								<a
									href="https://www.forallmoonkind.org"
									rel="noreferrer noopener"
									target="_blank"
								>
									For All Moonkind
								</a>{' '}
								registry.
							</p>

							<p>Participate & Contact</p>
							<p>Contact: info [at] beyondtheearth.org</p>
							<p>
								<img
									src={BeyondTheEarthLogo}
									width={70}
									height={68}
									alt="beyong the earth logo"
								/>
								<img
									src={EuroplanetLogo}
									width={62}
									height={70}
									alt="europlanet logo"
								/>
							</p>
						</div>
					</div>
					<div className="menu-item">
						<div className="menu-title">
							<a
								href="http://stuffin.space/"
								target="_blank"
								rel="noopener noreferrer"
							>
								Debris
							</a>
						</div>
					</div>
				</div>
				<CSSTransition
					in={leftPanel === LeftPanel.SEARCH}
					out={leftPanel !== LeftPanel.SEARCH}
					timeout={250}
					classNames="left-transition-"
				>
					<Search />
				</CSSTransition>

				<TransitionGroup>
					{leftPanel === LeftPanel.LEGEND && (
						<CSSTransition
							timeout={250}
							classNames="left-transition-"
						>
							<Legend />
						</CSSTransition>
					)}

					{leftPanel === LeftPanel.REFERENCES && (
						<CSSTransition
							timeout={250}
							classNames="left-transition-"
						>
							<References />
						</CSSTransition>
					)}
				</TransitionGroup>

				<div id="sat-hoverbox">(none)</div>
				{activeSatellite && <InfoBox satellite={activeSatellite} />}
				<div id="zoom-controls">
					<div id="zoom-in" className="zoom-button">
						+
					</div>
					<div id="zoom-out" className="zoom-button">
						-
					</div>
				</div>
				<div id="load-cover" />
			</div>
		</>
	);
};
